import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import Avatar from '../Avatar/Avatar';
import Col from '../../components/GridSystem/Col';
import { Card } from '../Card';
import CardTitle from '../Card/CardTitle';
import CardMeta from '../Card/CardMeta';
import CardMetaContent from '../Card/CardMetaContent';
import styles from './CardRow.module.scss';
import Checkbox from '../Checkbox/Checkbox';
import DotsBtn from '../Menu/DotsBtn';
import cx from 'classnames';
import CardMenu from '../CardMenu/CardMenu';
import { useLibraryContext } from '../../context/LibraryContext';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../context/UserContext';
import { approvalStatuses } from '../../features/Dashboard/components/VideosFilter/constants';
import { parseString } from '../../utils/stringHelper';
import { MAX_NAME_LENGTH } from '../../../common/constants/common';
import { Link } from 'react-router-dom';
import FadeInComponent from '../FadeInComponent/FadeInComponent';
import { useMountComponentWithDelay } from '../../hooks/useMountComponentWithDelay';
import { AnimationWrapper } from '../AnimationWrapper/AnimationWrapper';

const CardRow = ({
  menuRef,
  listMode,
  video,
  folder,
  videoIndex,
  searchInput,
  toggleShowMenu,
  renameVideoData,
  renameVideo,
  handleCopyClick,
  shareVideo,
  removeFromReview,
  duplicateVideo,
  downloadVideo,
  archiveVideos,
  moveVideoAction,
  unarchiveVideo,
  handleDotsClick,
  showMenu,
  onRenameInputChange,
  handleCardClick,
  deleteVideo,
  renameVideoAction,
  approveVideo,
  declineVideo,
  revokeVideo,
  isUserHasEditingRights,
  isUserHasAdvancedEditingRights,
  getRedirectLink,
  hideActions,
  alt = '',
}) => {
  const [hoverCard, setHoverCard] = useState({});
  const [showHoverState, setShowHoverState] = useState();
  const [init, setInit] = useState(false);

  const { user, isApprovalFlowEnabled } = useUserContext();

  const showCheckbox = useMemo(() => hoverCard.isHover && hoverCard.index === videoIndex, [hoverCard, videoIndex]);

  const isVideoAuthor = useMemo(() => user?.username === parseString(video.author.username), [user, video]);
  const isUserCanDoAction = useMemo(() => isVideoAuthor || isUserHasEditingRights, [isVideoAuthor, isUserHasEditingRights]);
  const isUserCanDoAdvancedAction = useMemo(() => isVideoAuthor || isUserHasAdvancedEditingRights, [
    isVideoAuthor,
    isUserHasAdvancedEditingRights,
  ]);

  useMountComponentWithDelay(hoverCard.isHover, 250, () => {
    if (hoverCard.isHover) {
      setShowHoverState(true);
    }
  });

  /* These statuses are needed to display or hide action buttons if isApprovalFlowEnabled is true
   * 1) 'Rename button' will be displayed when video's approvalStatus is 'in Review' or 'Approved'
   * 2) 'Share button' will be displayed only when video's approvalStatus is 'Approved'
   * 3) 'Copy link button' will be displayed only when video's approvalStatus is 'Approved'
   */

  const isPreExistedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.PRE_EXISTED.TYPE, [video]);
  const isInReviewStatus = useMemo(() => video?.approvalStatus === approvalStatuses.IN_REVIEW.TYPE, [video]);
  const isApprovedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.APPROVED.TYPE, [video]);

  const isProcessing = useMemo(() => !video.draft && video.thumbnail === null, [video]);

  const redirectLink = useMemo(() => getRedirectLink(video, folder), [video, folder]);

  const toggleMouseEnterCard = videoIndex => {
    setHoverCard({ index: videoIndex, isHover: true });
  };

  const toggleMouseLeaveCard = videoIndex => {
    setHoverCard({ index: videoIndex, isHover: false });
  };

  const { selectedCard } = useLibraryContext();

  const { t } = useTranslation();

  const handleShareVideo = shareVideo
    ? useCallback(
      id => {
        shareVideo(id);
        setHoverCard({});
      },
      [shareVideo, setHoverCard]
    )
    : undefined;

  const onCardClick = event => {
    if (!redirectLink) {
      window.open(video?.videoLink, '_blank');
      return;
    }

    const classList = Array.from(event?.target?.parentElement?.classList).join('');
    const isLinkClick = classList.includes('titleList');
    if (!isLinkClick) {
      handleCardClick(event, videoIndex);
    }
  };

  useEffect(() => {
    setInit(true);
  }, []);

  return (
    <Col key={video.id} xs={12} className={styles.listCol}>
      <Card
        id={videoIndex}
        data-cy="video"
        onClick={onCardClick}
        className={cx({
          [styles.activeCard]: selectedCard[video.id],
          [styles.renameError]: renameVideoData[video.id]?.error,
        })}
        listMode
        onMouseEnter={() => toggleMouseEnterCard(videoIndex)}
        onMouseLeave={() => toggleMouseLeaveCard(videoIndex)}
      >
        <div className={styles.cardHeader}>
          <div className={styles.absolute}>
            <AnimationWrapper show={showCheckbox || selectedCard[video.id]} options={{ duration: 250 }}>
              <Checkbox className={styles.activeCheckbox} checked={selectedCard[video.id]} />
            </AnimationWrapper>
          </div>
          <div className={styles.absolute}>
            <AnimationWrapper
              show={!showCheckbox && !selectedCard[video.id]}
              from={{ opacity: 0, ...(init ? { transform: 'scale(0.5)' } : {}) }}
              to={{ opacity: 1, ...(init ? { transform: 'scale(1)' } : {}) }}
              options={{ duration: 200 }}
              transformOrigin={'center'}
            >
              <Avatar className={styles.userAvatar} image={video?.author?.avatar} title={video?.author?.name} size="small" alt={alt} />
            </AnimationWrapper>
          </div>
        </div>
        <div className={styles.titleContainer}>
          {renameVideoData && renameVideoData[video.id] ? (
            <input
              autoFocus
              className={cx(styles.rename, styles.listView)}
              maxLength={MAX_NAME_LENGTH}
              type="text"
              onChange={e => onRenameInputChange(e, video.id)}
              value={renameVideoData[video.id].name}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  renameVideoAction(video.id);
                }
              }}
              onBlur={() => renameVideoAction(video.id)}
            />
          ) : (
            <>
              {redirectLink ? (
                <Link to={redirectLink} className={styles.link}>
                  <CardTitle>
                    <Highlighter
                      highlightClassName="wordHighlighter"
                      className={styles.titleList}
                      searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                      autoEscape
                      textToHighlight={video.name}
                    />
                  </CardTitle>
                </Link>
              ) : (
                <CardTitle>
                  <Highlighter
                    highlightClassName="wordHighlighter"
                    className={styles.titleList}
                    searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                    autoEscape
                    textToHighlight={video.name}
                  />
                </CardTitle>
              )}
            </>
          )}
        </div>
        <div className={styles.metaContainer}>
          <CardMeta>
            <CardMetaContent>
              <div className={styles.username}>
                <span>{parseString(video?.author?.name)}</span>
              </div>
            </CardMetaContent>
          </CardMeta>
        </div>
        <div className={styles.listDate}>
          <span>{t('general.date', { date: new Date(`${video.createDate}`) })}</span>
        </div>
        <div className={styles.lastEdited}>
          <span>{t('general.date', { date: new Date(`${video.updateDate}`) })}</span>
        </div>
        <div className={styles.listViews}>
          <span>
            {video?.stats?.views || 0} {video?.stats?.views === 1 ? t('general.view') : t('general.views')}
          </span>
        </div>
        {!hideActions && (
          <div className={styles.dotsMenu}>
            {hoverCard.isHover && hoverCard.index === videoIndex && (
              <FadeInComponent onHover={showHoverState}>
                <DotsBtn onClick={() => handleDotsClick(video.id)} />
              </FadeInComponent>
            )}
            {showMenu.uuid === video.id && showMenu.flag ? (
              <CardMenu
                menuRef={menuRef}
                listMode={listMode}
                item={video}
                itemIndex={videoIndex}
                toggleShowMenu={toggleShowMenu}
                renameItem={
                  !isProcessing &&
                  (!isApprovalFlowEnabled || (!isInReviewStatus && !isApprovedStatus)) &&
                  isUserCanDoAction &&
                  renameVideo
                }
                moveItem={isUserCanDoAdvancedAction && moveVideoAction}
                handleCopyClick={
                  !isProcessing && (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && handleCopyClick
                }
                shareItem={!isProcessing && (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && handleShareVideo}
                removeFromReview={!isProcessing && isApprovalFlowEnabled && isInReviewStatus && isUserCanDoAction && removeFromReview}
                duplicateItem={!isProcessing && (video.author.username === user.username || isUserHasEditingRights) && duplicateVideo}
                downloadItem={
                  !isProcessing && {
                    action: (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && downloadVideo,
                  }
                }
                archiveItem={isVideoAuthor && archiveVideos}
                unarchiveItem={isUserCanDoAction && unarchiveVideo}
                deleteVideo={isUserCanDoAdvancedAction && deleteVideo}
                approveVideo={!isProcessing && approveVideo}
                declineVideo={!isProcessing && declineVideo}
                revokeVideo={!isProcessing && revokeVideo}
                rightOffset={true}
              />
            ) : null}
          </div>
        )}
      </Card>
    </Col>
  );
};

export default CardRow;
